import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Tag, Card, Typography, Row, Col, Input, Modal, message, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlayCircleOutlined, PoweroffOutlined } from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { PlayoutServer, PlayoutServerStatus } from './types';
import PlayoutServerForm from './PlayoutServerForm';
import { ComponentTitle } from '../custom-components';
import { getPlayoutServerStatusIcon, getPlayoutServerStatusColor } from './utils';

const { Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

const PlayoutServerManager: React.FC = () => {
  const [servers, setServers] = useState<PlayoutServer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedServer, setSelectedServer] = useState<PlayoutServer | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [refreshingHealth, setRefreshingHealth] = useState<string | null>(null);
  const [startingInstance, setStartingInstance] = useState<string | null>(null);
  const [stoppingInstance, setStoppingInstance] = useState<string | null>(null);
  const [refreshingInstanceState, setRefreshingInstanceState] = useState<string | null>(null);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const serversRef = useRef<PlayoutServer[]>([]);

  // Keep serversRef in sync with servers state
  useEffect(() => {
    serversRef.current = servers;
  }, [servers]);

  useEffect(() => {
    loadServers();
    
    // Set up auto-refresh when component mounts
    autoRefreshIntervalRef.current = setInterval(() => {
      refreshAllServers();
    }, 60000); // Refresh every 60 seconds (1 minute)

    // Cleanup on unmount
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, []);

  const loadServers = async () => {
    setLoading(true);
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers`
      });
      setServers(response.data.servers || []);
    } catch (error) {
      console.error('Error loading playout servers:', error);
      message.error('Failed to load playout servers');
    } finally {
      setLoading(false);
    }
  };

  const refreshAllServers = async () => {
    try {
      // Use current servers from ref (always up-to-date)
      const currentServers = serversRef.current;
      
      if (currentServers.length === 0) {
        return; // No servers to refresh
      }
      
      // Refresh health and instance state for all servers (silently, no loading states)
      const refreshPromises = currentServers.map(async (server: PlayoutServer) => {
        const promises: Promise<void>[] = [];
        
        // Refresh health check for all servers
        promises.push(
          authAxios({
            method: 'POST',
            url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/health`
          }).then(() => {
            // Explicitly return void
          }).catch((error) => {
            console.error(`Error refreshing health for server ${server.id}:`, error);
          })
        );
        
        // Refresh instance state for servers with EC2 instance IDs (excluding local)
        if (server.ec2_instance_id && server.ec2_instance_id !== 'local') {
          promises.push(
            authAxios({
              method: 'GET',
              url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/instance-state`
            }).then(() => {
              // Explicitly return void
            }).catch((error) => {
              console.error(`Error refreshing instance state for server ${server.id}:`, error);
            })
          );
        }
        
        return Promise.all(promises);
      });
      
      // Wait for all refreshes to complete
      await Promise.all(refreshPromises);
      loadServers();
    } catch (error) {
      console.error('Error refreshing all servers:', error);
    }
  };

  const handleCreateServer = () => {
    setSelectedServer(null);
    setShowForm(true);
  };

  const handleEditServer = (server: PlayoutServer) => {
    setSelectedServer(server);
    setShowForm(true);
  };

  const handleDeleteServer = (server: PlayoutServer) => {
    confirm({
      title: 'Delete Playout Server',
      content: `Are you sure you want to delete "${server.display_name || server.name}"? This will unassign it from any streams.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await authAxios({
            method: 'DELETE',
            url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}`
          });
          message.success('Playout server deleted successfully');
          loadServers();
        } catch (error) {
          console.error('Error deleting playout server:', error);
          message.error('Failed to delete playout server');
        }
      }
    });
  };

  const handleRefreshHealth = async (server: PlayoutServer) => {
    setRefreshingHealth(server.id);
    try {
      await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/health`
      });
      message.success('Health check completed');
      loadServers();
    } catch (error) {
      console.error('Error refreshing health:', error);
      message.error('Failed to refresh health check');
    } finally {
      setRefreshingHealth(null);
    }
  };

  const handleRefreshInstanceState = async (server: PlayoutServer) => {
    if (!server.ec2_instance_id) {
      message.warning('EC2 instance ID is not set for this server');
      return;
    }

    setRefreshingInstanceState(server.id);
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/instance-state`
      });
      message.success(response.data.instanceState?.message || 'Instance state refreshed');
      loadServers();
    } catch (error: any) {
      console.error('Error refreshing instance state:', error);
      message.error(error.response?.data?.error || 'Failed to refresh instance state');
    } finally {
      setRefreshingInstanceState(null);
    }
  };

  const handleStartInstance = async (server: PlayoutServer) => {
    if (!server.ec2_instance_id) {
      message.warning('EC2 instance ID is not set for this server');
      return;
    }

    setStartingInstance(server.id);
    try {
      const response = await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/start-instance`
      });
      message.success(response.data.message || 'Instance start initiated');
      loadServers();
    } catch (error: any) {
      console.error('Error starting instance:', error);
      message.error(error.response?.data?.error || 'Failed to start instance');
    } finally {
      setStartingInstance(null);
    }
  };

  const handleStopInstance = async (server: PlayoutServer) => {
    if (!server.ec2_instance_id) {
      message.warning('EC2 instance ID is not set for this server');
      return;
    }

    confirm({
      title: 'Stop EC2 Instance',
      content: `Are you sure you want to stop the EC2 instance for "${server.name}"? This will shut down the server.`,
      okText: 'Stop',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setStoppingInstance(server.id);
        try {
          const response = await authAxios({
            method: 'POST',
            url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/playout-servers/${server.id}/stop-instance`
          });
          message.success(response.data.message || 'Instance stop initiated');
          loadServers();
        } catch (error: any) {
          console.error('Error stopping instance:', error);
          message.error(error.response?.data?.error || 'Failed to stop instance');
        } finally {
          setStoppingInstance(null);
        }
      }
    });
  };

  const getInstanceStateColor = (state?: string): string => {
    if (!state) return 'default';
    const lowerState = state.toLowerCase();
    if (lowerState === 'running') return 'green';
    if (lowerState === 'stopped') return 'red';
    if (lowerState === 'local') return 'default';
    if (['pending', 'starting', 'stopping'].includes(lowerState)) return 'orange';
    return 'default';
  };

  const getInstanceStateDisplay = (state?: string): string => {
    if (!state) return 'N/A';
    if (state === 'local') return 'Local Development';
    return state.charAt(0).toUpperCase() + state.slice(1);
  };


  const filteredServers = servers.filter(server => {
    const matchesSearch = !searchText ||
      (server.display_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
      server.name.toLowerCase().includes(searchText.toLowerCase()) ||
      server.endpoint_url.toLowerCase().includes(searchText.toLowerCase()) ||
      server.description?.toLowerCase().includes(searchText.toLowerCase());
    return matchesSearch;
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: PlayoutServerStatus) => (
        <Space>
          {getPlayoutServerStatusIcon(status)}
          <Tag color={getPlayoutServerStatusColor(status)}>
            {status?.toUpperCase()}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Server',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: PlayoutServer) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            {record.display_name || text}
          </div>
          {record.display_name && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '1px' }}>
              {text}
            </div>
          )}
          {record.description && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Endpoint URL',
      dataIndex: 'endpoint_url',
      key: 'endpoint_url',
      render: (url: string) => (
        <Text code style={{ fontSize: '12px' }}>{url}</Text>
      ),
    },
    {
      title: 'EC2 Instance ID',
      dataIndex: 'ec2_instance_id',
      key: 'ec2_instance_id',
      width: 150,
      render: (instanceId: string) => {
        if (!instanceId) {
          return <Text type="secondary">N/A</Text>;
        }
        return <Text code style={{ fontSize: '12px' }}>{instanceId}</Text>;
      },
    },
    {
      title: 'Last Health Check',
      dataIndex: 'last_health_check',
      key: 'last_health_check',
      render: (date: string) => date ? new Date(date).toLocaleString() : 'Never',
    },
    {
      title: 'EC2 Instance State',
      dataIndex: 'ec2_instance_state',
      key: 'ec2_instance_state',
      width: 150,
      render: (state: string, record: PlayoutServer) => {
        if (!record.ec2_instance_id) {
          return <Tag color="default">N/A</Tag>;
        }
        return (
          <Tag color={getInstanceStateColor(state)}>
            {getInstanceStateDisplay(state)}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 300,
      render: (text: any, record: PlayoutServer) => {
        const hasEC2Instance = !!record.ec2_instance_id;
        const isLocal = record.ec2_instance_id === 'local';
        const canStart = hasEC2Instance && !isLocal && 
          (record.ec2_instance_state === 'stopped' || record.ec2_instance_state === 'stopping' || !record.ec2_instance_state);
        const canStop = hasEC2Instance && !isLocal && 
          (record.ec2_instance_state === 'running' || record.ec2_instance_state === 'starting');

        return (
          <Space>
            <Tooltip title="Refresh Health">
              <Button 
                type="text" 
                icon={<ReloadOutlined />} 
                onClick={() => handleRefreshHealth(record)}
                loading={refreshingHealth === record.id}
              />
            </Tooltip>
            {hasEC2Instance && (
              <Tooltip title={isLocal ? "Local instances cannot be controlled" : "Refresh EC2 instance state"}>
                <Button 
                  type="text" 
                  icon={<ReloadOutlined />} 
                  onClick={() => handleRefreshInstanceState(record)}
                  loading={refreshingInstanceState === record.id}
                  disabled={isLocal}
                />
              </Tooltip>
            )}
            {canStart && (
              <Tooltip title="Start EC2 instance">
                <Button 
                  type="text" 
                  icon={<PlayCircleOutlined />} 
                  onClick={() => handleStartInstance(record)}
                  loading={startingInstance === record.id}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
            )}
            {canStop && (
              <Tooltip title="Stop EC2 instance">
                <Button 
                  type="text" 
                  danger
                  icon={<PoweroffOutlined />} 
                  onClick={() => handleStopInstance(record)}
                  loading={stoppingInstance === record.id}
                />
              </Tooltip>
            )}
            <Tooltip title="Edit Server">
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={() => handleEditServer(record)}
              />
            </Tooltip>
            <Tooltip title="Delete Server">
              <Button 
                type="text" 
                danger
                icon={<DeleteOutlined />} 
                onClick={() => handleDeleteServer(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <ComponentTitle>Playout Servers</ComponentTitle>
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleCreateServer}
          >
            Add Playout Server
          </Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={18}>
            <Search
              placeholder="Search servers by name, URL, or description..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Button onClick={loadServers} style={{ width: '100%' }}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredServers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} servers`,
          }}
        />
      </Card>

      <PlayoutServerForm
        server={selectedServer}
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedServer(null);
        }}
        onSave={() => {
          setShowForm(false);
          setSelectedServer(null);
          loadServers();
        }}
      />
    </div>
  );
};

export default PlayoutServerManager;




